import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  IconButton,
  Typography,
  Snackbar,
  Alert,
  Container,
  Paper,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Lock as LockIcon,
} from "@mui/icons-material";
import { useDebouncedValue } from "hooks/useDebouncedValue";
import {
  useGetPublicCirclesQuery,
  useRequestToJoinMutation,
  type PublicCircleSortField,
  type SortOrder,
} from "components/Slices/circleApiSlice";
import { useGetStatusQuery } from "components/Slices/usersApiSlice";

import {
  PublicCircleCard,
  LoadingSkeleton,
  EmptyState,
  FilterBar,
  DiscoverHeader,
} from "./components";
import { DEFAULT_PAGE_LIMIT, SEARCH_DEBOUNCE_MS } from "./constants";
import type { CircleRequestStates, SnackbarState } from "./types";
import { sandooqTheme } from "themes/sandooqTheme";

const PublicCirclesTab: React.FC = () => {
  const navigate = useNavigate();

  // Pagination State
  const [page, setPage] = useState(1);

  // Filter & Sort State
  const [sortBy, setSortBy] = useState<PublicCircleSortField | "">("");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [searchInput, setSearchInput] = useState("");

  // UI State
  const [requestStates, setRequestStates] = useState<CircleRequestStates>({});
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: "",
    severity: "success",
  });

  const debouncedSearch = useDebouncedValue(
    searchInput.trim(),
    SEARCH_DEBOUNCE_MS
  );

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, sortBy, sortOrder]);

  // Query
  const { data, isLoading, isFetching } = useGetPublicCirclesQuery({
    limit: DEFAULT_PAGE_LIMIT,
    page,
    sortBy: sortBy || undefined,
    sortOrder: sortBy ? sortOrder : undefined,
    search: debouncedSearch || undefined,
  });

  const [requestToJoin] = useRequestToJoinMutation();

  // Fetch user status to check if onboarding is complete
  const { data: userStatusData } = useGetStatusQuery();
  const PARTICIPATABLE_STATUSES = ["active", "complete", "pending", "verifying", "verification_failed"];
  const isUserActive = PARTICIPATABLE_STATUSES.includes(userStatusData?.userStatus?.toLowerCase() ?? "");

  // Handlers
  const handleClearAll = useCallback(() => {
    setSortBy("");
    setSearchInput("");
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchInput("");
  }, []);

  const handleRequestJoin = useCallback(
    async (circleId: string) => {
      setRequestStates((prev) => ({ ...prev, [circleId]: "sending" }));

      try {
        await requestToJoin({ circleId }).unwrap();
        setRequestStates((prev) => ({ ...prev, [circleId]: "sent" }));
        setSnackbar({
          open: true,
          message: "Request sent! The admin will review your request.",
          severity: "success",
        });
      } catch (error: any) {
        setRequestStates((prev) => ({ ...prev, [circleId]: "error" }));
        const message =
          error?.data?.message || "Failed to send request. Please try again.";
        setSnackbar({
          open: true,
          message,
          severity: "error",
        });
      }
    },
    [requestToJoin]
  );

  const handleViewDetails = useCallback(
    (circleId: string) => {
      navigate(`/circles/${circleId}`);
    },
    [navigate]
  );

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  const circles = data?.circles || [];
  const totalPages = data?.totalPages || 1;
  const hasPrev = page > 1;
  const hasNext = page < totalPages;

  return (
    <Box>
      <DiscoverHeader />
      {!isUserActive && (
        <Alert
          severity="warning"
          icon={<LockIcon fontSize="small" />}
          sx={{
            width: "100%",
            py: 0.5,
            "& .MuiAlert-message": { fontSize: "0.75rem" },
          }}
        >
          You must complete your{" "}
          <a
            href="/onboarding?step=1"
            style={{ color: "inherit", textDecoration: "underline", fontWeight: 600 }}
          >
            contact information
          </a>{" "}
          before you can join a circle.
        </Alert>
      )}
      <FilterBar
        searchInput={searchInput}
        onSearchChange={setSearchInput}
        onClearSearch={handleClearSearch}
        sortBy={sortBy}
        onSortByChange={setSortBy}
        sortOrder={sortOrder}
        onSortOrderChange={setSortOrder}
        onClearAll={handleClearAll}
      />

      {isLoading ? (
        <LoadingSkeleton />
      ) : circles.length === 0 ? (
        <EmptyState />
      ) : (
        <Paper square elevation={0}  sx={{
          p: 2,
          mb: 3,
          borderRadius: 2,
          border: `1px solid ${sandooqTheme.palette.divider}`,
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }} >
          <Grid container spacing={3}>
            {circles.map((circle) => (
              <Grid item xs={12} sm={6} md={4} key={circle.id}>
                <PublicCircleCard
                  circle={circle}
                  requestState={
                    circle.status || requestStates[circle.id] || "idle"
                  }
                  onRequestJoin={handleRequestJoin}
                  onViewDetails={handleViewDetails}
                  isRequesting={isFetching}
                  isUserActive={isUserActive}
                />
              </Grid>
            ))}
          </Grid>
          {totalPages > 1 && (
            <Box
              sx={{
                mt: 4,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 2,
              }}
            >
              <IconButton
                onClick={() => setPage((p) => p - 1)}
                disabled={!hasPrev || isFetching}
                sx={{
                  border: 1,
                  borderColor: "divider",
                  "&:disabled": { opacity: 0.4 },
                }}
              >
                <ArrowBackIcon />
              </IconButton>

              <Typography variant="body2" color="text.secondary">
                Page {page} of {totalPages}
              </Typography>

              <IconButton
                onClick={() => setPage((p) => p + 1)}
                disabled={!hasNext || isFetching}
                sx={{
                  border: 1,
                  borderColor: "divider",
                  "&:disabled": { opacity: 0.4 },
                }}
              >
                <ArrowForwardIcon />
              </IconButton>
            </Box>
          )}
        </Paper>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PublicCirclesTab;
